import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useSnack } from "../../../../hooks/useSnack";
import SnackbarComponent from "../../../../components/Snackbar/Snackbar";
import {
    getPartnerByDomain,
    reservationLogin,
    PartnerInfo,
    ReservationLoginResponse,
} from "../../../../actions/reservation-auth-actions";
import { AuthContext } from "../../../../contexts/auth-context";
import { setStoredUser } from "../../../../util/storage-helpers";
import ContactDetailsModal from "../../../../components/ContactDetails/ContactDetailsModal";
import { LANGUAGES } from "../../../../i18n";
import "./ReservationLogin.scss";

function ReservationLogin() {
    const { partnerDomain } = useParams<{ partnerDomain: string }>();
    const [partner, setPartner] = useState<PartnerInfo | null>(null);
    const [reservationNumber, setReservationNumber] = useState("");
    const [doorLockCode, setDoorLockCode] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [partnerNotFound, setPartnerNotFound] = useState(false);

    // Contact details modal state
    const [showContactModal, setShowContactModal] = useState(false);
    const [pendingLoginData, setPendingLoginData] = useState<ReservationLoginResponse | null>(null);

    const snack = useSnack();
    const navigate = useNavigate();
    const { setUser } = useContext(AuthContext);
    const { t, i18n } = useTranslation();

    const changeLanguage = (langCode: string) => {
        i18n.changeLanguage(langCode);
    };

    const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

    useEffect(() => {
        const fetchPartner = async () => {
            if (!partnerDomain) {
                setPartnerNotFound(true);
                setIsLoading(false);
                return;
            }

            try {
                const response = await getPartnerByDomain(partnerDomain);
                setPartner(response.data);
            } catch {
                setPartnerNotFound(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPartner();
    }, [partnerDomain]);

    const completeLogin = (loginData: ReservationLoginResponse, contactFirstName?: string, contactLastName?: string, contactEmail?: string) => {
        const { token, guestName, guestEmail, unitName, unitPhotoUrl, unitAddress, checkIn, checkOut, partnerId, partnerName, partnerLogo, wifiNetworkName, wifiPassword, doorLockCode: doorCode, unitAddressDetails } = loginData;

        // Use contact details from modal if provided, otherwise use data from login response
        const finalName = contactFirstName && contactLastName
            ? `${contactFirstName} ${contactLastName}`
            : guestName;
        const finalEmail = contactEmail || guestEmail;

        const userData = {
            token,
            name: finalName,
            email: finalEmail,
            unitName,
            unitPhotoUrl,
            unitAddress,
            unitAddressDetails,
            checkIn,
            checkOut,
            partnerId,
            partnerName,
            partnerLogo,
            wifiNetworkName,
            wifiPassword,
            doorLockCode: doorCode,
            authType: "reservation",
            expiresAt: loginData.expiresAt,
        };

        // Store user data in localStorage (includes token)
        setStoredUser(userData);

        // Set user in context
        setUser(userData);

        snack.setMessage(t("login.welcomeRedirecting"));
        snack.setSeverity("success");
        snack.onOpen();

        // Navigate to home
        setTimeout(() => {
            navigate("/admin/home");
        }, 500);
    };

    const handleContactDetailsSaved = (firstName: string, lastName: string, email: string) => {
        setShowContactModal(false);
        if (pendingLoginData) {
            completeLogin(pendingLoginData, firstName, lastName, email);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!reservationNumber || !doorLockCode) {
            snack.setMessage(t("login.fillAllFields"));
            snack.setSeverity("warning");
            snack.onOpen();
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await reservationLogin({
                partnerDomain: partnerDomain!,
                reservationNumber,
                doorLockCode,
            });

            const loginData = response.data;

            // Store the token immediately so the contact details API call works
            const tempUserData = {
                token: loginData.token,
                authType: "reservation",
                expiresAt: loginData.expiresAt,
            };
            setStoredUser(tempUserData);

            // Check if we need to collect contact details
            if (!loginData.hasContactDetails) {
                // Show modal to collect contact details
                setPendingLoginData(loginData);
                setShowContactModal(true);
            } else {
                // Contact details already on file, proceed with login
                completeLogin(loginData);
            }
        } catch (error: any) {
            const errorData = error.response?.data;
            const message = typeof errorData === 'string'
                ? errorData
                : errorData?.message || t("login.invalidCredentials");
            snack.setMessage(message);
            snack.setSeverity("error");
            snack.onOpen();
        } finally {
            setIsSubmitting(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="login-page dark-theme">
                <div className="login-card">
                    <div className="login-loading">
                        <div className="loading-spinner" />
                        <p>{t("common.loading")}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Partner not found state
    if (partnerNotFound) {
        return (
            <div className="login-page dark-theme">
                <div className="login-card">
                    <div className="login-error">
                        <div className="login-error__icon">
                            <FiX size={64} />
                        </div>
                        <h2 className="login-error__title">{t("login.propertyNotFound")}</h2>
                        <p className="login-error__message">
                            {t("login.propertyNotFoundMessage", { domain: partnerDomain })}
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="login-footer">
                    <img
                        src="https://steasyhubprodus.blob.core.windows.net/public/logo-white.png"
                        alt="EasyHub Prime Pass"
                        className="login-footer__logo"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="login-page dark-theme">
            <div className="login-card">
                {/* Logo */}
                <div className="login-logo">
                    {partner?.logo ? (
                        <img
                            src={partner.logo}
                            alt={partner.name}
                            className="login-logo__image"
                        />
                    ) : (
                        <h1 className="login-logo__text">{partner?.name || "Prime Pass"}</h1>
                    )}
                </div>

                {/* Welcome Text */}
                <div className="login-welcome">
                    <h1 className="login-welcome__title">{t("login.welcome")}</h1>
                    <p className="login-welcome__subtitle">
                        {t("login.subtitle")}
                    </p>
                </div>

                {/* Login Form */}
                <form className="login-form" onSubmit={handleSubmit}>
                    <div className="login-form__group">
                        <label className="login-form__label" htmlFor="reservationNumber">
                            {t("login.reservationNumber")}
                        </label>
                        <input
                            id="reservationNumber"
                            className="login-form__input"
                            placeholder={t("login.reservationPlaceholder")}
                            type="text"
                            value={reservationNumber}
                            onChange={(e) => setReservationNumber(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="login-form__group">
                        <label className="login-form__label" htmlFor="doorLockCode">
                            {t("login.doorLockCode")}
                        </label>
                        <input
                            id="doorLockCode"
                            className="login-form__input"
                            placeholder={t("login.doorLockPlaceholder")}
                            type="text"
                            value={doorLockCode}
                            onChange={(e) => setDoorLockCode(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>

                    <button
                        className="login-form__button"
                        type="submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="loading-spinner" />
                                {t("login.signingIn")}
                            </>
                        ) : (
                            t("login.accessGuestPortal")
                        )}
                    </button>

                    <p className="login-form__help">
                        {t("login.helpText")}
                    </p>
                </form>

                {/* Footer */}
                <div className="login-footer">
                    {/* Language Toggle */}
                    <div className="login-footer__language">
                        {LANGUAGES.map((lang) => (
                            <button
                                key={lang.code}
                                className={`login-footer__language-btn ${
                                    currentLanguage.code === lang.code ? 'login-footer__language-btn--active' : ''
                                }`}
                                onClick={() => changeLanguage(lang.code)}
                                type="button"
                            >
                                <span className="login-footer__language-flag">{lang.flag}</span>
                            </button>
                        ))}
                    </div>
                    <img
                        src="https://steasyhubprodus.blob.core.windows.net/public/logo-white.png"
                        alt="EasyHub Prime Pass"
                        className="login-footer__logo"
                    />
                </div>
            </div>

            {/* Contact Details Modal */}
            <ContactDetailsModal
                isOpen={showContactModal}
                initialFirstName={pendingLoginData?.contactFirstName}
                initialLastName={pendingLoginData?.contactLastName}
                initialEmail={pendingLoginData?.contactEmail}
                onSaved={handleContactDetailsSaved}
            />

            {/* Snackbar */}
            <SnackbarComponent
                isOpen={snack.isOpen}
                severity={snack.severity}
                onClose={snack.onClose}
                message={snack.message}
            />
        </div>
    );
}

export default ReservationLogin;
